import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { createPortal } from 'react-dom'
import { useLeftToolbar } from './LeftToolbar.jsx'

// Separación entre la barra y el menú.
const GAP_PX = 8
// Lo que se deja libre abajo y arriba para que el menú no toque el borde.
const EDGE_MARGIN_PX = 8

/**
 * Botón de la barra izquierda que despliega un menú a su derecha.
 *
 * Sustituye al `dropdown dropdown-right` de DaisyUI, que se dibuja dentro de la
 * zona con scroll de la barra y allí quedaría recortado. El menú se pinta en la
 * capa de la barra (ver `LeftToolbar`), alineado con el botón y sin salirse de
 * la pantalla; se cierra al pulsar fuera, con Escape o al desplazar la barra.
 */
const SideMenu = ({ tip, icon, children, menuClassName = '', as: Menu = 'div' }) => {
    const layer = useLeftToolbar()
    const triggerRef = useRef(null)
    const menuRef = useRef(null)
    const [open, setOpen] = useState(false)
    const [position, setPosition] = useState({ top: 0 })

    const place = () => {
        const trigger = triggerRef.current
        const menu = menuRef.current
        if (!layer || !trigger) return
        const triggerBox = trigger.getBoundingClientRect()
        const layerBox = layer.getBoundingClientRect()
        let top = triggerBox.top - layerBox.top
        if (menu) {
            // Si por abajo no cabe, el menú sube lo justo; nunca por encima del borde de arriba.
            const overflow = triggerBox.top + menu.offsetHeight - (window.innerHeight - EDGE_MARGIN_PX)
            if (overflow > 0) top -= overflow
            top = Math.max(top, EDGE_MARGIN_PX - layerBox.top)
        }
        setPosition({ top })
    }

    // Con el menú ya pintado se conoce su alto y se recoloca.
    useLayoutEffect(() => {
        if (open) place()
    }, [open, children])

    useEffect(() => {
        if (!open) return undefined
        const onPointerDown = (event) => {
            if (menuRef.current?.contains(event.target) || triggerRef.current?.contains(event.target)) return
            setOpen(false)
        }
        const onKeyDown = (event) => { if (event.key === 'Escape') setOpen(false) }
        const close = () => setOpen(false)
        document.addEventListener('pointerdown', onPointerDown)
        window.addEventListener('keydown', onKeyDown)
        window.addEventListener('resize', close)
        // Cualquier desplazamiento de la barra (`scroll` no sube: se captura).
        layer?.addEventListener('scroll', close, true)
        return () => {
            document.removeEventListener('pointerdown', onPointerDown)
            window.removeEventListener('keydown', onKeyDown)
            window.removeEventListener('resize', close)
            layer?.removeEventListener('scroll', close, true)
        }
    }, [open, layer])

    return (
        <>
            <button
                ref={triggerRef}
                type="button"
                className="btnMod cursor-pointer"
                data-tip={tip}
                aria-label={tip}
                aria-haspopup="true"
                aria-expanded={open}
                onClick={() => setOpen((value) => !value)}
            >
                {icon}
            </button>
            {open && layer && createPortal(
                <Menu
                    ref={menuRef}
                    className={`customiz3r-leftbar__menu shadow bg-base-100 rounded-box ${menuClassName}`}
                    style={{ top: position.top }}
                >
                    {children}
                </Menu>,
                layer,
            )}
        </>
    )
}

SideMenu.propTypes = {
    tip: PropTypes.string.isRequired,
    icon: PropTypes.node.isRequired,
    children: PropTypes.node,
    menuClassName: PropTypes.string,
    as: PropTypes.elementType,
}

export default SideMenu
